import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconRefresh,
  IconDownload,
  IconUpload,
  IconCopy,
  IconTrash,
  IconEdit,
  IconPlus,
  IconCalendar,
  IconMapPin,
  IconUser,
  IconClock,
  IconBuilding,
  IconSettings
} from '@tabler/icons-react';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface ToastConfig {
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  data?: Record<string, any>;
}

interface ToastSystemConfig {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxNotifications?: number;
  zIndex?: number;
  autoClose?: number;
}

/**
 * Enhanced Toast Notification System
 * 
 * Features from UX Flow Spec:
 * - Success toasts for completed actions
 * - Error toasts for failures with actionable messages
 * - Info toasts for status updates
 * - Warning toasts for conflicts and issues
 * - Loading toasts for async operations
 * - Contextual icons for different actions
 * - Actionable buttons for quick responses
 * - Consistent styling and positioning
 */
class EnhancedToastSystem {
  private config: ToastSystemConfig;

  constructor(config: ToastSystemConfig = {}) {
    this.config = {
      position: 'top-right',
      maxNotifications: 5,
      zIndex: 1000,
      autoClose: 4000,
      ...config
    };

    // Note: Mantine notifications are configured via the <Notifications> component
    // in the JSX, not through a configure method. The configuration is handled
    // in the App.tsx and main.tsx files.
  }

  private getIcon = (type: ToastType, context?: string) => {
    // Context-specific icons
    if (context) {
      switch (context) {
        case 'assignment':
          return <IconCalendar size={16} />;
        case 'room':
          return <IconMapPin size={16} />;
        case 'doctor':
          return <IconUser size={16} />;
        case 'schedule':
          return <IconClock size={16} />;
        case 'building':
          return <IconBuilding size={16} />;
        case 'export':
          return <IconDownload size={16} />;
        case 'import':
          return <IconUpload size={16} />;
        case 'copy':
          return <IconCopy size={16} />;
        case 'delete':
          return <IconTrash size={16} />;
        case 'edit':
          return <IconEdit size={16} />;
        case 'create':
          return <IconPlus size={16} />;
        case 'settings':
          return <IconSettings size={16} />;
        case 'refresh':
          return <IconRefresh size={16} />;
      }
    }

    // Default type icons
    switch (type) {
      case 'success':
        return <IconCheck size={16} />;
      case 'error':
        return <IconX size={16} />;
      case 'warning':
        return <IconAlertTriangle size={16} />;
      case 'info':
        return <IconInfoCircle size={16} />;
      case 'loading':
        return <IconRefresh size={16} />;
      default:
        return <IconInfoCircle size={16} />;
    }
  };

  private getColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'green';
      case 'error':
        return 'red';
      case 'warning':
        return 'yellow';
      case 'info':
        return 'blue';
      case 'loading':
        return 'blue';
      default:
        return 'blue';
    }
  };

  private showToast = (config: ToastConfig) => {
    const { type, title, message, duration = this.config.autoClose, action, data } = config;
    
    const notificationConfig: any = {
      id: `toast-${Date.now()}-${Math.random()}`,
      title,
      message,
      color: this.getColor(type),
      icon: this.getIcon(type, data?.['context']),
      autoClose: type === 'loading' ? false : duration,
      loading: type === 'loading',
      withCloseButton: type !== 'loading',
      ...data
    };
    
    if (action) {
      notificationConfig.onClose = () => action.onClick();
    }
    
    notifications.show(notificationConfig);
  };

  // =============================================================================
  // SUCCESS TOASTS
  // =============================================================================

  assignmentCreated = (assignmentTitle: string) => {
    this.showToast({
      type: 'success',
      title: 'Assignment Created',
      message: `Successfully created assignment: ${assignmentTitle}`,
      data: { context: 'assignment' }
    });
  };

  assignmentUpdated = (assignmentTitle: string) => {
    this.showToast({
      type: 'success',
      title: 'Assignment Updated',
      message: `Successfully updated assignment: ${assignmentTitle}`,
      data: { context: 'assignment' }
    });
  };

  assignmentDeleted = (assignmentTitle: string) => {
    this.showToast({
      type: 'success',
      title: 'Assignment Deleted',
      message: `Successfully deleted assignment: ${assignmentTitle}`,
      data: { context: 'assignment' }
    });
  };

  doctorAssigned = (doctorName: string, roomNumber: string) => {
    this.showToast({
      type: 'success',
      title: 'Doctor Assigned',
      message: `Dr. ${doctorName} assigned to Room ${roomNumber}`,
      data: { context: 'assignment' }
    });
  };

  doctorUnassigned = (doctorName: string, roomNumber: string) => {
    this.showToast({
      type: 'success',
      title: 'Doctor Unassigned',
      message: `Dr. ${doctorName} unassigned from Room ${roomNumber}`,
      data: { context: 'assignment' }
    });
  };

  schedulePublished = (dateRange: string, assignmentCount: number) => {
    this.showToast({
      type: 'success',
      title: 'Schedule Published',
      message: `Published ${assignmentCount} assignments for ${dateRange}`,
      data: { context: 'schedule' }
    });
  };

  scheduleCopied = (sourceDate: string, targetCount: number) => {
    this.showToast({
      type: 'success',
      title: 'Schedule Copied',
      message: `Copied schedule from ${sourceDate} to ${targetCount} target dates`,
      data: { context: 'copy' }
    });
  };

  recurrenceCreated = (ruleType: string, occurrenceCount: number) => {
    this.showToast({
      type: 'success',
      title: 'Recurrence Created',
      message: `Created ${ruleType} recurrence with ${occurrenceCount} occurrences`,
      data: { context: 'schedule' }
    });
  };

  exportCompleted = (format: string, filename: string) => {
    this.showToast({
      type: 'success',
      title: 'Export Completed',
      message: `Exported data to ${filename} (${format})`,
      data: { context: 'export' }
    });
  };

  dataRefreshed = () => {
    this.showToast({
      type: 'success',
      title: 'Data Refreshed',
      message: 'Planner data has been updated successfully',
      data: { context: 'refresh' }
    });
  };

  // =============================================================================
  // ERROR TOASTS
  // =============================================================================

  assignmentFailed = (error: string, suggestion?: string) => {
    this.showToast({
      type: 'error',
      title: 'Assignment Failed',
      message: `${error}${suggestion ? ` - ${suggestion}` : ''}`,
      duration: 6000,
      data: { context: 'assignment' }
    });
  };

  conflictDetected = (conflictType: string, details: string) => {
    this.showToast({
      type: 'error',
      title: 'Conflict Detected',
      message: `${conflictType}: ${details}`,
      duration: 8000,
      action: {
        label: 'View Conflicts',
        onClick: () => {
          // TODO: Open conflicts panel
          console.log('Open conflicts panel');
        }
      },
      data: { context: 'assignment' }
    });
  };

  publishFailed = (error: string, conflictCount?: number) => {
    this.showToast({
      type: 'error',
      title: 'Publish Failed',
      message: `${error}${conflictCount ? ` (${conflictCount} conflicts found)` : ''}`,
      duration: 6000,
      data: { context: 'schedule' }
    });
  };

  exportFailed = (error: string) => {
    this.showToast({
      type: 'error',
      title: 'Export Failed',
      message: `Failed to export data: ${error}`,
      duration: 5000,
      data: { context: 'export' }
    });
  };

  networkError = (operation: string) => {
    this.showToast({
      type: 'error',
      title: 'Network Error',
      message: `Failed to ${operation}. Please check your connection and try again.`,
      duration: 8000,
      action: {
        label: 'Retry',
        onClick: () => {
          // TODO: Implement retry logic
          console.log('Retry operation');
        }
      },
      data: { context: 'network' }
    });
  };

  validationError = (field: string, message: string) => {
    this.showToast({
      type: 'error',
      title: 'Validation Error',
      message: `${field}: ${message}`,
      duration: 5000,
      data: { context: 'validation' }
    });
  };

  // =============================================================================
  // WARNING TOASTS
  // =============================================================================

  capacityExceeded = (roomNumber: string, currentCount: number, maxCapacity: number) => {
    this.showToast({
      type: 'warning',
      title: 'Room Capacity Exceeded',
      message: `Room ${roomNumber}: ${currentCount}/${maxCapacity} capacity exceeded`,
      duration: 6000,
      data: { context: 'room' }
    });
  };

  doctorDoubleBooked = (doctorName: string, conflictingSessions: string[]) => {
    this.showToast({
      type: 'warning',
      title: 'Doctor Double Booked',
      message: `Dr. ${doctorName} is assigned to ${conflictingSessions.length} conflicting sessions`,
      duration: 6000,
      data: { context: 'doctor' }
    });
  };

  roomBlocked = (roomNumber: string, blockReason: string, endDate: string) => {
    this.showToast({
      type: 'warning',
      title: 'Room Blocked',
      message: `Room ${roomNumber} is blocked (${blockReason}) until ${endDate}`,
      duration: 6000,
      data: { context: 'room' }
    });
  };

  pastSessionLocked = (sessionDate: string) => {
    this.showToast({
      type: 'warning',
      title: 'Past Session Locked',
      message: `Cannot modify past session from ${sessionDate}. Sessions are read-only after the date.`,
      duration: 6000,
      data: { context: 'schedule' }
    });
  };

  // =============================================================================
  // INFO TOASTS
  // =============================================================================

  filtersApplied = (filterCount: number) => {
    this.showToast({
      type: 'info',
      title: 'Filters Applied',
      message: `${filterCount} filters are currently active`,
      duration: 3000,
      data: { context: 'settings' }
    });
  };

  filtersCleared = () => {
    this.showToast({
      type: 'info',
      title: 'Filters Cleared',
      message: 'All filters have been reset to default values',
      duration: 3000,
      data: { context: 'settings' }
    });
  };

  viewChanged = (viewType: string) => {
    this.showToast({
      type: 'info',
      title: 'View Changed',
      message: `Switched to ${viewType} view`,
      duration: 2000,
      data: { context: 'settings' }
    });
  };

  dateChanged = (newDate: string) => {
    this.showToast({
      type: 'info',
      title: 'Date Changed',
      message: `Viewing schedule for ${newDate}`,
      duration: 2000,
      data: { context: 'schedule' }
    });
  };

  bulkOperationStarted = (operation: string, itemCount: number) => {
    this.showToast({
      type: 'info',
      title: 'Bulk Operation Started',
      message: `${operation} operation started on ${itemCount} items`,
      duration: 3000,
      data: { context: 'assignment' }
    });
  };

  bulkOperationCompleted = (operation: string, successCount: number, totalCount: number) => {
    this.showToast({
      type: 'info',
      title: 'Bulk Operation Completed',
      message: `${operation}: ${successCount}/${totalCount} items processed successfully`,
      duration: 4000,
      data: { context: 'assignment' }
    });
  };

  // =============================================================================
  // LOADING TOASTS
  // =============================================================================

  loadingData = (operation: string) => {
    this.showToast({
      type: 'loading',
      title: 'Loading...',
      message: `${operation} in progress`,
      duration: 0, // Don't auto-close loading toasts
      data: { context: 'loading' }
    });
  };

  processingBulkOperation = (operation: string, processedCount: number, totalCount: number) => {
    this.showToast({
      type: 'loading',
      title: 'Processing...',
      message: `${operation}: ${processedCount}/${totalCount} items completed`,
      duration: 0,
      data: { context: 'loading' }
    });
  };

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  dismissAll = () => {
    notifications.clean();
  };

  dismissById = (id: string) => {
    notifications.hide(id);
  };

  updateConfig = (newConfig: Partial<ToastSystemConfig>) => {
    this.config = { ...this.config, ...newConfig };
  };
}

// Create singleton instance
const toastSystem = new EnhancedToastSystem({
  position: 'top-right',
  maxNotifications: 5,
  autoClose: 4000
});

export default toastSystem;
export { EnhancedToastSystem };
